import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, doc, query, where, onSnapshot } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useLocation } from "react-router-dom";
import { LineWave } from 'react-loader-spinner'
import QRCode from "react-qr-code";

import './App.css';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function App() {
  const id = useQuery().get("id");
  const [appData, setAppData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const [access, setAccess] = useState({ allowed: false, checking: true });

  useEffect(() => {
    let unsubscribeMembers;
    const unsubscribeAuth = onAuthStateChanged(getAuth(), (user) => {
      unsubscribeMembers?.();
      setAccess({ allowed: false, checking: Boolean(user?.email) });
      if (!user?.email) return;
      unsubscribeMembers = onSnapshot(doc(db, "Settings", "Members"), (snapshot) => {
        const testers = snapshot.data()?.testers;
        setAccess({ allowed: Array.isArray(testers) && testers.includes(user.email), checking: false });
      }, () => setAccess({ allowed: false, checking: false }));
    }, () => {
      unsubscribeMembers?.();
      setAccess({ allowed: false, checking: false });
    });
    return () => {
      unsubscribeAuth();
      unsubscribeMembers?.();
    };
  }, []);

  const isRestricted = appData?.isDisabled === true;
  const canViewApp = appData && (!isRestricted || access.allowed);
  const accessDenied = !loading && isRestricted && !access.checking && !access.allowed;
  const hasLoadError = error && error !== "This app is not available.";

  useEffect(() => {
    setAppData(null);
    setError("");
    setLoading(true);
    setCopySuccess(false);
    if (!id) {
      setError("This app is not available.");
      setLoading(false);
      return;
    }

    const q = query(collection(db, "App"), where("refId", "==", id));
    return onSnapshot(q, (querySnapshot) => {
      const snapshot = querySnapshot.docs[0];
      if (!snapshot) {
        setAppData(null);
        setError("This app is not available.");
      } else {
        setAppData({
          ...snapshot.data(),
          isManaged: snapshot.id.endsWith("-managed"),
        });
        setError("");
      }
      setLoading(false);
    }, (error) => {
      setAppData(null);
      setError("Unable to load release details: " + error.message);
      setLoading(false);
    });
  }, [id]);

  const handleDownload = async () => {
    const anchor = document.createElement("a");
    anchor.href = appData.downloadUrl;
    anchor.click();
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(appData.downloadUrl);
    setCopySuccess(true);
    window.setTimeout(() => setCopySuccess(false), 2000);
  };

  const formatDate = (date) => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };

    const jsDate = date instanceof Date ? date : date.toDate();

    return new Intl.DateTimeFormat('en-US', options).format(jsDate);
  };

  const appDescription = appData?.isManaged
    ? "This managed build is intended for deployment by your organization and does not support in-app updates."
    : "Download this release or scan the QR code to open the download link on your device.";

  const scanDescription = appData?.isManaged
    ? "Scan the QR code to download the managed package for your organization."
    : "Scan the QR code with your device’s camera to open the download link.";

  return (
    <div className="app-page" style={{ '--app-accent': appData?.accentColor || '#fe8100' }}>
      <header className="app-page-header">
        <img src={require(`./abadata_logo_light.png`)} alt="AbaData" className="logo" />
        <span className="app-header-label">App distribution</span>
      </header>
      <main className="main-app">
        {canViewApp ? (
          <div className="app-shell">
            <section className="app-hero-card">
              <div className="app-hero-copy">
                <p className="app-eyebrow">Current release</p>
                <div className="app-title-row">
                  {appData.appIconUrl ? (
                    <img
                      className="appIcon"
                      src={appData.appIconUrl}
                      alt={`${appData.appName} icon`}
                    />
                  ) : null}
                  <div>
                    <div className="app-name-row">
                      <h1 className="app-name" >{appData.appName}</h1>
                      {appData.isManaged && <span className="app-pill managed">Managed</span>}
                    </div>
                    <p className="version">Version {appData.versionName} ({appData.versionCode})</p>
                  </div>
                </div>
                <p className="app-description">
                  {appDescription}
                </p>
              </div>

              <div className="app-actions-card">
                <p className="app-action-label">Download package</p>
                <button
                  className="app-download-button"
                  onClick={handleDownload}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12m-5-5 5 5 5-5M4 16v5h16v-5" /></svg>
                  Download app
                </button>
                <button className="copy-button" onClick={handleCopyUrl}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V4H4v12h4" /></svg>
                  {copySuccess ? "Link copied" : "Copy download link"}
                </button>
                <p className="app-package-summary">Version {appData.versionName} ({appData.versionCode}) · {appData.fileSize} MB</p>
              </div>
            </section>

            <section className="app-content-grid">
              <div className="qr-section">
                <p className="app-section-kicker">On your device</p>
                <div className="qr-frame">
                  <QRCode value={appData.downloadUrl} size={192} />
                </div>
                <p className="qr-title">Scan to download</p>
                <p className="scan-text">{scanDescription}</p>
              </div>

              <div className="details">
                <p className="app-section-kicker">Package information</p>
                <h2 className="details-title">Release details</h2>
                <div className="detail-item">
                  <label>File size</label>
                  <p>{appData.fileSize} MB</p>
                </div>
                <div className="detail-item">
                  <label>Version name</label>
                  <p>{appData.versionName}</p>
                </div>
                <div className="detail-item">
                  <label>Version code</label>
                  <p>{appData.versionCode}</p>
                </div>
                <div className="detail-item" style={{ paddingBottom: 0 }}>
                  <label>Last updated</label>
                  <p>{formatDate(appData.updatedTime)}</p>
                </div>
              </div>
            </section>
          </div>
        ) : null}
        {(loading || (isRestricted && access.checking)) && (
          <div className="loading">
            <LineWave
              visible={true}
              height="100"
              width="100"
              color="#FE8100"
              ariaLabel="Loading release details"
              wrapperStyle={{}}
              wrapperClass=""
              firstLineColor=""
              middleLineColor=""
              lastLineColor=""
            />
          </div>
        )}
        {(error || accessDenied) && (
          <section className="app-unavailable" aria-labelledby="unavailable-title">
            <div className="app-unavailable-icon">
              <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="7" width="30" height="34" rx="7" />
                <path d="M19 20a5 5 0 0 1 10 0c0 4-5 4-5 8" /><path d="M24 33h.01" />
              </svg>
            </div>
            <p className="app-unavailable-eyebrow">App distribution</p>
            <h1 id="unavailable-title">{hasLoadError ? 'Unable to load app' : 'App not found'}</h1>
            <p>{hasLoadError ? 'We couldn’t load this release. Please try again in a moment.' : 'This app is not available.'}</p>
            {hasLoadError && <div className="app-unavailable-actions">
              <button onClick={() => window.location.reload()}>Try again</button>
            </div>}
          </section>
        )}
      </main>
    </div>
  );
}

export default App;
